'use client';

import { memo, useEffect, useId, useRef, useCallback } from 'react';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { PhoneInput as ReactPhoneInput } from 'react-international-phone';

const phoneUtil = PhoneNumberUtil.getInstance();

export const isPhoneValid = (phone) => {
	if (!phone) return false;

	try {
		const parsedNumber = phoneUtil.parseAndKeepRawInput(phone);
		return phoneUtil.isValidNumber(parsedNumber);
	} catch {
		return false;
	}
};

const formatPlaceholder = (placeholder, hasLabel, required) => {
	if (!placeholder) return placeholder;

	if (hasLabel) {
		return placeholder.replace(/\s*\*+\s*$/, '');
	}

	if (required && !placeholder.includes('*')) {
		return `${placeholder}*`;
	}

	return placeholder;
};

export const PhoneInput = memo(function PhoneInput({
	value,
	onChange,
	onBlur,
	label,
	error,
	required,
	placeholder,
	className,
	disabled = false,
	autoComplete = 'tel',
	defaultCountry = 'ua',
}) {
	const normalizedPlaceholder = formatPlaceholder(placeholder, Boolean(label), required);
	const inputId = useId();
	const containerRef = useRef(null);

	const handleChange = useCallback(
		(phone, meta) => {
			onChange(phone, meta);
		},
		[onChange],
	);

	useEffect(() => {
		if (!containerRef.current) return undefined;

		const handleCountrySelect = (event) => {
			const target = event.target;

			if (
				target.classList?.contains('react-international-phone-country-selector-dropdown__list-item') ||
				target.closest?.('.react-international-phone-country-selector-dropdown__list-item')
			) {
				requestAnimationFrame(() => {
					const button = containerRef.current?.querySelector('.react-international-phone-country-selector-button');

					if (button?.getAttribute('aria-expanded') === 'true') {
						button.click();
					}
				});
			}
		};

		const container = containerRef.current;
		container.addEventListener('click', handleCountrySelect, { passive: true });

		return () => {
			container.removeEventListener('click', handleCountrySelect);
		};
	}, []);

	return (
		<div ref={containerRef} className={`crm-quiz-phone ${className || ''}`.trim()}>
			{label ? (
				<span className="crm-quiz-phone__label">
					{label}
					{required ? <span className="crm-quiz-phone__required">*</span> : null}
				</span>
			) : null}
			<ReactPhoneInput
				defaultCountry={defaultCountry}
				value={value}
				onChange={handleChange}
				disabled={disabled}
				className={`crm-quiz-phone__input${error ? ' is-invalid' : ''}`}
				preferredCountries={['ua', 'pl', 'gb', 'us', 'de', 'ge', 'az', 'es']}
				inputProps={{
					id: inputId,
					onBlur,
					placeholder: normalizedPlaceholder,
					autoComplete,
					type: 'text',
					inputMode: 'tel',
					disabled,
				}}
			/>
			{/* {error ? <span className="wpcf7-not-valid-tip crm-quiz-phone__error error">{error}</span> : null} */}
		</div>
	);
});
